import { useMemo, useRef, useEffect } from "react";
import { AbsoluteFill, useCurrentFrame, useVideoConfig } from "remotion";
import { AudioTrack } from "../../types/schema";
import {
	AudioVisualizerProps,
	CROSSFADE_DURATION_FRAMES,
	defaultVisualizer,
	VISUALIZER_LAYOUT,
} from "../constants";
import { useVisualizerData } from "../hooks/use-visualizer-data";
import { getTrackSchedule } from "../utils/audio";

interface ComponentProps {
	audioTracks: AudioTrack[];
	visualizer: AudioVisualizerProps["visualizer"];
	isTest?: boolean;
	withTitle?: boolean;
}

export const AudioVisualizer: React.FC<ComponentProps> = ({
	audioTracks,
	visualizer,
	isTest,
	withTitle,
}) => {
	// Destructure config
	const {
		position = defaultVisualizer.position,
		barWidth = defaultVisualizer.barWidth,
		gap = defaultVisualizer.gap,
		maxHeight = defaultVisualizer.maxHeight,
		color = defaultVisualizer.color,
		barsToDisplay = defaultVisualizer.barsToDisplay,
	} = visualizer;

	const frame = useCurrentFrame();
	const { width, height, fps } = useVideoConfig();
	const canvasRef = useRef<HTMLCanvasElement>(null);

	// Constants
	const {
		MARGIN_SIDE,
		PADDING_BOTTOM_WITH_TITLE,
		PADDING_BOTTOM_NO_TITLE,
		TEXT_BOTTOM_OFFSET,
	} = VISUALIZER_LAYOUT;

	// Ensure we have durations for all tracks, defaulting to 30s (900 frames)
	const safeTrackDurations = useMemo(() => {
		return audioTracks.map((track) => {
			return track.durationInSeconds
				? Math.ceil(track.durationInSeconds * fps)
				: 30 * fps;
		});
	}, [audioTracks, fps]);

	const schedule = useMemo(
		() => getTrackSchedule(safeTrackDurations, CROSSFADE_DURATION_FRAMES),
		[safeTrackDurations],
	);

	// Get visualized data from hook
	const smoothedBars = useVisualizerData({
		audioTracks,
		schedule,
		barsToDisplay,
		isTest,
	});

	// Layout calculation
	const totalBarWidth = barsToDisplay * barWidth + (barsToDisplay - 1) * gap;

	let startX = (width - totalBarWidth) / 2;
	if (position === "bottom-left") startX = MARGIN_SIDE;
	else if (position === "bottom-right")
		startX = width - totalBarWidth - MARGIN_SIDE;

	const bottomY = withTitle
		? height - PADDING_BOTTOM_WITH_TITLE
		: height - PADDING_BOTTOM_NO_TITLE;

	const currentTitle = useMemo(() => {
		if (!withTitle) return null;
		// Prefer the latest started track in case of overlaps
		const active = [...schedule]
			.reverse()
			.find((s) => frame >= s.startFrame && frame < s.endFrame);
		return active ? audioTracks[active.index]?.title : null;
	}, [withTitle, schedule, frame, audioTracks]);

	// Draw on canvas - optimized to reduce redundant draws
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext("2d", {
			alpha: true,
			desynchronized: true, // Improve performance
		});
		if (!ctx) return;

		// Clear only the bar area, not entire canvas
		ctx.clearRect(0, bottomY - maxHeight - 10, width, maxHeight + 50);

		// Set style
		ctx.fillStyle = color;

		// Draw bars with reduced complexity
		smoothedBars.forEach((v, i) => {
			const h = Math.sqrt(v) * maxHeight;
			if (h <= 1) return;

			const finalHeight = Math.min(maxHeight, Math.max(0, h));
			const x = startX + i * (barWidth + gap);
			const y = bottomY - finalHeight;

			// Simple rectangle instead of rounded (faster)
			ctx.fillRect(x, y, barWidth, finalHeight);
		});
	}, [smoothedBars, width, startX, bottomY, color, barWidth, gap, maxHeight]);

	return (
		<AbsoluteFill>
			<canvas
				ref={canvasRef}
				width={width}
				height={height}
				style={{
					width: "100%",
					height: "100%",
				}}
			/>
			{withTitle && currentTitle && (
				<div
					style={{
						position: "absolute",
						bottom: TEXT_BOTTOM_OFFSET,
						width: position === "bottom-center" ? "100%" : "auto",
						left: position === "bottom-left" ? MARGIN_SIDE : undefined,
						right: position === "bottom-right" ? MARGIN_SIDE : undefined,
						textAlign:
							position === "bottom-left"
								? "left"
								: position === "bottom-right"
									? "right"
									: "center",
						color,
						fontFamily: "sans-serif",
						fontSize: 24,
						fontWeight: 600,
						textTransform: "uppercase",
						letterSpacing: "0.1em",
						opacity: 0.9,
					}}
				>
					{currentTitle}
				</div>
			)}
		</AbsoluteFill>
	);
};
